// NOTE: We are not using it now so we can delete this file in future.
// But for right now we need it only for knowledge. Let's keep it for now.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { notificationAdd } from "../actions/notification";
import { createEmergency, getEmergencyList, patchEmergency } from "../api/emergency";
import { getActiveIPDs } from "../api/ipd";
import { Emergency } from "../interfaces/EmergencyInterface";
import { getWards, postWard, transferEmergencyToIpd, transferIpdBed } from "../api/wards";
import { postBeds, getBeds } from "../api/bed";
import { IPD, TriagePriority } from "../interfaces/IpdInterface";
import { AppDispatch, RootState } from "../store";
import { Ward } from "../interfaces/WardInterface";
import { Bed } from "../interfaces/BedInterface";

interface IState {
  wards: Array<Ward>;
  beds: Array<Bed>;
  activeIPDs: Array<Partial<IPD>>;
  emergencyRecords: Array<Emergency>;
  emergencyIdMap: Record<number, number>;
  triagePriorityMap: Record<number, TriagePriority>;
}

export interface WardPayload {
  id: number | null;
  name: string;
  shortCOde: string;
  isRoom: boolean;
}

const initialState: IState = {
  wards: [],
  beds: [],
  activeIPDs: [],
  emergencyRecords: [],
  emergencyIdMap: {},
  triagePriorityMap: {}
};

const mapEmergencyTriageToPriority = (
  triage?: Emergency["triage"] | null
): TriagePriority | null => {
  if (triage === "red") return TriagePriority.Critical;
  if (triage === "yellow") return TriagePriority.Urgent;
  if (triage === "green") return TriagePriority.Normal;
  return null;
};

const mapPriorityToEmergencyTriage = (priority: TriagePriority): Emergency["triage"] => {
  if (priority === TriagePriority.Critical) return "red";
  if (priority === TriagePriority.Urgent) return "yellow";
  return "green";
};

const syncEmergencyState = (draft: IState) => {
  draft.triagePriorityMap = {};
  draft.emergencyIdMap = {};

  draft.activeIPDs.forEach((ipd) => {
    const matchingEmergency = draft.emergencyRecords.find(
      (emergency) =>
        emergency.active &&
        ipd.active &&
        emergency.bedId === ipd.bedId &&
        emergency.wardId === ipd.wardId &&
        (!ipd.client?.id || emergency.clientId === ipd.client.id)
    );

    if (!matchingEmergency || !ipd.id) return;

    const priority = mapEmergencyTriageToPriority(matchingEmergency.triage);
    if (priority) {
      draft.triagePriorityMap[ipd.id] = priority;
      draft.emergencyIdMap[ipd.id] = matchingEmergency.id;
    }
  });
};

export const fetchWards = createAsyncThunk("wards", async (_, { rejectWithValue, dispatch }) => {
  try {
    return await getWards();
  } catch (e: any) {
    dispatch(
      notificationAdd({
        id: new Date().getUTCMilliseconds(),
        variant: "error",
        message: "Failed to fetch wards",
        autoTimeout: true
      })
    );
    return rejectWithValue(e);
  }
});

export const fetchBeds = createAsyncThunk("beds", async (_, { rejectWithValue, dispatch }) => {
  try {
    const response = await getBeds();
    return response.data || [];
  } catch (e) {
    dispatch(
      notificationAdd({
        id: new Date().getUTCMilliseconds(),
        variant: "error",
        message: "Failed to fetch beds",
        autoTimeout: true
      })
    );
    return rejectWithValue(e);
  }
});

export const createWards = createAsyncThunk(
  "wards/create",
  async (data: Partial<WardPayload>, { rejectWithValue, dispatch }) => {
    try {
      return await postWard(data);
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Failed to create ward",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const createBeds = createAsyncThunk(
  "beds/create",
  async (data: Partial<Bed>, { rejectWithValue, dispatch }) => {
    try {
      const response = await postBeds(data);
      return response.data || [];
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Failed to create beds",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const fetchActiveIpds = createAsyncThunk(
  "ipd/active",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await getActiveIPDs();
      return res.data;
    } catch (e) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Failed to get active ipds",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const fetchEmergencyRecords = createAsyncThunk(
  "emergency/list",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      return await getEmergencyList();
    } catch (e) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Failed to fetch emergency records",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

async function ensureEmergencyRecordForIpd(
  ipdId: number,
  priority: TriagePriority,
  dispatch: AppDispatch,
  getState: () => RootState
) {
  let state = getState() as { bedManagement: IState };
  let emergencyId = state.bedManagement.emergencyIdMap[ipdId];

  if (emergencyId) {
    return emergencyId;
  }

  await dispatch(fetchEmergencyRecords());
  state = getState() as { bedManagement: IState };
  emergencyId = state.bedManagement.emergencyIdMap[ipdId];

  if (emergencyId) {
    return emergencyId;
  }

  const activeIpd = state.bedManagement.activeIPDs.find((ipd) => ipd.id === ipdId);

  if (!activeIpd?.client?.id || !activeIpd.wardId || !activeIpd.bedId) {
    throw new Error("Emergency record not found for this bed.");
  }

  const createdEmergency = await createEmergency({
    clientId: activeIpd.client.id,
    wardId: activeIpd.wardId,
    bedId: activeIpd.bedId,
    triage: mapPriorityToEmergencyTriage(priority),
    dateOfAdmission: activeIpd.dateOfAdmission,
    gcsScore: 15
  });

  return {
    emergencyId: createdEmergency.id,
    createdEmergency
  };
}

export const saveEmergencyTriagePriority = createAsyncThunk(
  "emergency/triage",
  async (
    data: { ipdId: number; priority: TriagePriority },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const emergencyInfo = await ensureEmergencyRecordForIpd(
        data.ipdId,
        data.priority,
        dispatch,
        getState
      );

      let emergencyId: number;

      if (typeof emergencyInfo === "number") {
        emergencyId = emergencyInfo;
      } else {
        emergencyId = emergencyInfo.emergencyId;
      }

      const response = await patchEmergency(emergencyId, {
        triage: mapPriorityToEmergencyTriage(data.priority)
      });

      return {
        ipdId: data.ipdId,
        emergency: response
      };
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Failed to update triage priority",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const saveEmergencyBed = createAsyncThunk(
  "emergency/bed",
  async (
    data: { ipdId: number; wardId: number; bedId: number },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const emergencyInfo = await ensureEmergencyRecordForIpd(
        data.ipdId,
        TriagePriority.Normal,
        dispatch,
        getState
      );

      let emergencyId: number;

      if (typeof emergencyInfo === "number") {
        emergencyId = emergencyInfo;
      } else {
        emergencyId = emergencyInfo.emergencyId;
      }

      const response = await patchEmergency(emergencyId, {
        wardId: data.wardId,
        bedId: data.bedId
      });

      return response;
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Failed to update emergency bed",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const transferBed = createAsyncThunk(
  "beds/transfer",
  async (data: { ipdId: number; wardId: number; bedId: number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await transferIpdBed(data.ipdId, {
        wardId: data.wardId,
        bedId: data.bedId
      });
      return response;
    } catch (e) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Failed to transfer beds",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

export const transferEmergencyPatientToIpd = createAsyncThunk(
  "beds/transferEmergencyToIpd",
  async (data: { ipdId: number; wardId: number; bedId: number }, { rejectWithValue, dispatch }) => {
    try {
      const response = await transferEmergencyToIpd(data.ipdId, {
        wardId: data.wardId,
        bedId: data.bedId
      });
      dispatch(fetchEmergencyRecords());
      return response;
    } catch (e: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: e.message || "Failed to transfer patient to IPD",
          autoTimeout: true
        })
      );
      return rejectWithValue(e);
    }
  }
);

const wardSlice = createSlice({
  name: "bedManagement",
  initialState,
  reducers: {
    setActiveBed: (draft, action) => {
      draft.activeIPDs.push(action.payload);
    },
    replaceActiveIpd: (draft, action) => {
      const idx = draft.activeIPDs.findIndex((ipd) => ipd.id === action.payload.id);
      if (idx !== -1) draft.activeIPDs[idx] = action.payload;
      return draft;
    },
    updateWardData: (draft, action) => {
      const idx = draft.wards.findIndex((ward) => ward.id === action.payload.id);
      if (idx !== -1) {
        draft.wards[idx].name = action.payload.name;
      }
      return draft;
    },
    updateBedData: (draft, action) => {
      const idx = draft.beds.findIndex((bed) => bed.id === action.payload.id);
      if (idx !== -1) draft.beds[idx] = action.payload;
      return draft;
    },
    setTriagePriority: (
      draft,
      action: PayloadAction<{ ipdId: number; priority: TriagePriority }>
    ) => {
      draft.triagePriorityMap[action.payload.ipdId] = action.payload.priority;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWards.fulfilled, (draft, { payload }) => {
      draft.wards = Array.isArray(payload) ? payload : [];
      return draft;
    });

    builder.addCase(fetchBeds.fulfilled, (draft, { payload }) => {
      draft.beds = payload;
      return draft;
    });

    builder.addCase(createWards.fulfilled, (draft, { payload }) => {
      if (payload.ward) {
        draft.wards.push(payload.ward);
      }
      if (payload.beds?.length) {
        draft.beds = [...draft.beds, ...payload.beds];
      }
    });

    builder.addCase(createBeds.fulfilled, (draft, { payload }) => {
      draft.beds = [...draft.beds, ...payload];
    });

    builder.addCase(fetchActiveIpds.fulfilled, (draft, { payload }) => {
      draft.activeIPDs = Array.isArray(payload) ? payload : [];
      syncEmergencyState(draft);
      return draft;
    });

    builder.addCase(transferBed.fulfilled, (draft, { payload }) => {
      const idx = draft.activeIPDs.findIndex((ipd) => ipd.id === payload.id);
      if (idx !== -1) draft.activeIPDs[idx] = payload;
      syncEmergencyState(draft);
      return draft;
    });

    builder.addCase(transferEmergencyPatientToIpd.fulfilled, (draft, { payload }) => {
      const idx = draft.activeIPDs.findIndex((ipd) => ipd.id === payload.id);
      if (idx !== -1) draft.activeIPDs[idx] = payload;
      syncEmergencyState(draft);
      return draft;
    });

    builder.addCase(fetchEmergencyRecords.fulfilled, (draft, { payload }) => {
      draft.emergencyRecords = payload;
      syncEmergencyState(draft);
      return draft;
    });

    builder.addCase(saveEmergencyTriagePriority.fulfilled, (draft, { payload }) => {
      const idx = draft.emergencyRecords.findIndex((record) => record.id === payload.emergency.id);
      if (idx !== -1) {
        draft.emergencyRecords[idx] = payload.emergency;
      } else {
        draft.emergencyRecords.push(payload.emergency);
      }
      syncEmergencyState(draft);
      return draft;
    });

    builder.addCase(saveEmergencyBed.fulfilled, (draft, { payload }) => {
      const idx = draft.emergencyRecords.findIndex((record) => record.id === payload.id);
      if (idx !== -1) {
        draft.emergencyRecords[idx] = payload;
      } else {
        draft.emergencyRecords.push(payload);
      }
      syncEmergencyState(draft);
      return draft;
    });
  }
});

export default wardSlice.reducer;
export const { setActiveBed, replaceActiveIpd, updateWardData, updateBedData, setTriagePriority } =
  wardSlice.actions;
